export const monthMap: Record<string, string> = {
  januar: '01',
  februar: '02',
  mars: '03',
  april: '04',
  mai: '05',
  juni: '06',
  juli: '07',
  august: '08',
  september: '09',
  oktober: '10',
  november: '11',
  desember: '12',
}

// Upstream repealedByLawId can name the commencement act; keyed on the pair so an entry stops firing once upstream is corrected.
const REPEALED_BY_LAW_ID_OVERRIDES: Record<string, { from: string, to: string }> = {
  'lov-1902-05-22-10': { from: 'lov-2015-06-19-65', to: 'lov-2005-05-20-28' }, // straffeloven 1902: straffelovens ikraftsettingslov -> straffeloven 2005
}

export const LINK_HREF_REGEX = /<a[^>]*href=['"](lov-[^'"]*)['"][^>]*>[\s\S]*?opphevet[\s\S]*?<\/a>/i

// eslint-disable-next-line regexp/no-misleading-capturing-group
export const TEXT_PARSE_REGEX = /opphevet\s*(?:(ved|erstattet av)\s*)?lov\s*(\d{1,2})\.?\s*(\w+)\s*(\d{4})(?:\s*nr\.\s*(\d+))?/i

export function parseDateParts(yearString: string | undefined, monthStr: string | undefined, dayStr: string | undefined): { year: number, month: number, day: number } | null {
  if (!yearString || !monthStr || !dayStr) {
    return null
  }

  const year = Number.parseInt(yearString, 10)
  const month = Number.parseInt(monthStr, 10)
  const day = Number.parseInt(dayStr, 10)

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null
  }

  return { year, month, day }
}

export interface SuccessorLaw {
  successorLawDate: string
  successorLawNumber?: string
  successorLawTitle?: string
}

function inRange(value: number | undefined, from: number, to?: number): value is number {
  return typeof value === 'number' && value >= from && (typeof to !== 'number' || value <= to)
}

export function parseSuccessorLawDetails(successorLawId: string, newLawTitle?: string | null): SuccessorLaw | undefined {
  let successorLawDate: string | undefined
  let successorLawNumber: string | undefined

  const [prefix, ...parts] = successorLawId.split('-')
  if (parts.length >= 3 && prefix === 'lov') {
    const [y, m, d] = parts.slice(0, 3).map(Number)
    if (inRange(y, 1000) && inRange(m, 1, 12) && inRange(d, 1, 31)) {
      successorLawDate = formatDate(new Date(y, m - 1, d), { day: '2-digit', month: 'long', year: 'numeric' })
      successorLawNumber = parts.length > 3 ? parts[3] : undefined
    }
  }

  if (!successorLawDate) {
    return undefined
  }

  const lawPostfix = successorLawNumber
    ? `Lov ${successorLawDate} nr. ${successorLawNumber}`
    : `Lov ${successorLawDate}`

  const successorLawTitle = (newLawTitle ?? 'Lov').replace(/^Lov/, lawPostfix)

  return { successorLawDate, successorLawNumber, successorLawTitle }
}

export interface ResolvedSuccessorLaw {
  lawId: string
  title: string
  linkable: boolean
}

export function resolveSuccessorLaw(successorLawId: string | null | undefined, successorDoc: { id: string, title?: string | null } | undefined): ResolvedSuccessorLaw | undefined {
  if (!successorLawId) {
    return undefined
  }

  const isSuccessorInIndex = successorDoc?.id.toLowerCase() === successorLawId.toLowerCase()
  const title = parseSuccessorLawDetails(successorLawId, isSuccessorInIndex ? successorDoc?.title : undefined)?.successorLawTitle

  if (!title) {
    return undefined
  }

  return {
    lawId: successorLawId,
    title,
    // The law page returns 404 for hidden ids even when they are indexed.
    linkable: isSuccessorInIndex && !isHiddenLawId(successorLawId),
  }
}

export function effectiveSuccessorLawId(lawId: string | null | undefined, repealedByLawId: string | null | undefined): string | null | undefined {
  if (!lawId || !repealedByLawId) {
    return repealedByLawId
  }

  const override = REPEALED_BY_LAW_ID_OVERRIDES[lawId.toLowerCase()]
  if (override && override.from.toLowerCase() === repealedByLawId.toLowerCase()) {
    return override.to
  }

  return repealedByLawId
}
